import { DeliveryEnum } from '../interfaces'
import {
  DimensionMaxDto,
  PieceDimensionMax,
  RangeDeclareDto,
} from '../interfaces/General/RangelDeclareDto'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../interfaces/Import/ShipmentGroupInterface'

/**
 * Validación de grupos y piezas de importación, extraída de ShipmentBulkUploadReview
 * (deuda TODOS, eng-review E1) para reusarla en el Review y en la validación
 * pre-vuelo (#1). Función pura: la validación de grupo recibe su contexto explícito.
 *
 * Esta es la validación cliente que mirror-ea las reglas conocidas del backend; es la
 * base del pre-vuelo #1. La validez de account_code/mailbox (lookup backend) requiere
 * el endpoint dry-run (dependencia abierta).
 */

export const isValidCoord = (
  val: number | null,
  min: number,
  max: number,
): boolean => val !== null && !isNaN(val) && val >= min && val <= max

// ---------------------------------------------------------------------------
// Teléfono: paridad EXACTA con el validador del backend (is_phone_number).
// El backend arma `${countryPhoneAccessCode} ${digits}` (ej "+58 4141234567") y
// exige que matchee ^\+\d{1,3} \d{9,15}$. Como el código de país (+58) es fijo,
// esto se reduce a: los dígitos locales deben ser 9 a 15. Un FIJO de 8 dígitos
// (ej "07124922" → "+58 07124922") REVIENTA en el backend; esta es la causa raíz
// del error "is not a valid phone number" al enviar guías desde un favorito.
// ---------------------------------------------------------------------------
export const BACKEND_PHONE_RE = /^\+\d{1,3} \d{9,15}$/
export const PHONE_COUNTRY_CODE = '+58'

/** Mínimo y máximo de dígitos LOCALES que acepta el backend (regex de arriba). */
export const PHONE_MIN_LOCAL_DIGITS = 9
export const PHONE_MAX_LOCAL_DIGITS = 15

/**
 * Largo del teléfono LOCAL venezolano: 3 dígitos de código de área (fijo) o de
 * operadora (móvil) + 7 del número. Sin el 0 de marcación nacional.
 */
export const VE_LOCAL_DIGITS = 10

/** Compone el valor tal cual viaja al backend: `${countryCode} ${digits}`. */
export function composeConsigneePhone(
  digits: string | null | undefined,
  countryCode: string = PHONE_COUNTRY_CODE,
): string {
  return `${countryCode} ${(digits || '').trim()}`
}

/** True si el teléfono compuesto pasaría el validador del backend. */
export function isBackendValidPhone(
  digits: string | null | undefined,
  countryCode: string = PHONE_COUNTRY_CODE,
): boolean {
  return BACKEND_PHONE_RE.test(composeConsigneePhone(digits, countryCode))
}

// ---------------------------------------------------------------------------
// Código de país: el catálogo .NET (GET /Country) NO guarda solo el prefijo
// internacional. República Dominicana viene como "+1 809" — prefijo + código de
// área en el mismo campo. El backend exige `^\+\d{1,3} \d{9,15}$`, así que
// mandar "+1 809 5551234" (dos espacios) lo rechaza SIEMPRE.
//
// La solución en todo el front es la misma: el código es el PRIMER token, y los
// dígitos que sobren en el campo (el "809") son parte del número local.
// ---------------------------------------------------------------------------

/**
 * "+1 809" → `{ dial: '+1', extraDigits: '809' }`; "58" → `{ dial: '+58',
 * extraDigits: '' }`; vacío → `{ dial: '+58', extraDigits: '' }`.
 */
export function splitDialCode(raw: string | null | undefined): {
  dial: string
  extraDigits: string
} {
  const digits = (raw || '').replace(/\D/g, '')
  if (!digits) return { dial: PHONE_COUNTRY_CODE, extraDigits: '' }
  const firstToken = (raw || '').trim().split(/\s+/)[0].replace(/\D/g, '')
  // Un campo sin espacios ("+57") es todo prefijo. Con espacios, el primer token
  // manda y el resto son dígitos del número.
  const dialDigits = firstToken || digits
  return {
    dial: `+${dialDigits}`,
    extraDigits: digits.slice(dialDigits.length),
  }
}

/** Solo el prefijo internacional del campo del catálogo ("+1 809" → "+1"). */
export function normalizeDialCode(raw: string | null | undefined): string {
  return splitDialCode(raw).dial
}

/**
 * Código de país del teléfono del destinatario de una guía, con el default
 * aplicado.
 *
 * `consigneePhoneCountryCode` es OPCIONAL (así las guías viejas, los fixtures y
 * los payloads sin código siguen andando), y eso obliga a que cada lector
 * escriba el `|| '+58'`. Concentrarlo acá es lo que evita que el próximo lector
 * que alguien agregue se olvide y mande el país en `undefined`.
 */
export function consigneeCountryCode(g: {
  consigneePhoneCountryCode?: string
}): string {
  return g.consigneePhoneCountryCode || PHONE_COUNTRY_CODE
}

/**
 * Teléfono de una cuenta (`listAccountPhone[0]`) compuesto como lo espera el
 * backend, moviendo al número los dígitos que el catálogo dejó pegados al
 * código: `{'+1 809', '5551234'}` → `"+1 8095551234"`.
 */
export function composeAccountPhone(
  countryPhoneAccessCode: string | null | undefined,
  phoneNumber: string | null | undefined,
): string {
  const { dial, extraDigits } = splitDialCode(countryPhoneAccessCode)
  const digits = extraDigits + (phoneNumber || '').replace(/\D/g, '')
  return composeConsigneePhone(digits, dial)
}

// ---------------------------------------------------------------------------
// Teléfono: mensaje de error POR REGLA, no un booleano.
//
// Vive acá y no dentro de `validateGroup` porque hay dos caminos que necesitan el
// MISMO texto: la validación en vivo del editor, y la traducción del error de
// ejecución que devuelve el backend por detalle (`account.PhoneNumber.1`, que solo
// dice "no es válido" y trae el número). Sin una única fuente, el operador veía un
// mensaje distinto según por dónde llegara el error al mismo campo.
// ---------------------------------------------------------------------------

/**
 * Pista para el operador que pegó un número internacional en la celda de un
 * teléfono venezolano (12+ dígitos sin 0 inicial: el `+57` perdió el `+` y quedó
 * pegado al número). Dice qué hacer, no solo que está mal.
 */
export const INTERNATIONAL_PHONE_HINT =
  '¿Número internacional? Elegí el país y escribí el número sin el código'

/**
 * Mensaje del primer problema que tenga el teléfono (solo los dígitos locales, sin
 * el código de país), o `null` si pasa todas las reglas.
 *
 * Las reglas de operadora/FIJO son de VENEZUELA y solo corren con `+58`. Para
 * cualquier otro país el front no conoce el plan de numeración local (lo valida
 * el backend con libphonenumber), así que solo se exige lo que el backend exige
 * sí o sí: 9 a 15 dígitos.
 */
export function consigneePhoneError(
  phone: string | null | undefined,
  countryCode: string = PHONE_COUNTRY_CODE,
): string | null {
  const phoneRaw = (phone || '').trim()
  if (!phoneRaw) return 'Teléfono requerido'
  if (!/^\d+$/.test(phoneRaw)) return 'Solo dígitos permitidos'

  if ((countryCode || PHONE_COUNTRY_CODE) !== PHONE_COUNTRY_CODE) {
    if (phoneRaw.length < PHONE_MIN_LOCAL_DIGITS)
      return `El sistema no acepta números de menos de ${PHONE_MIN_LOCAL_DIGITS} dígitos (sin el código de país)`
    if (phoneRaw.length > PHONE_MAX_LOCAL_DIGITS)
      return `Teléfono demasiado largo (máximo ${PHONE_MAX_LOCAL_DIGITS} dígitos)`
    if (!isBackendValidPhone(phoneRaw, countryCode))
      return 'Teléfono inválido para el sistema'
    return null
  }

  // Venezuela: 3 dígitos de código de área (fijo, 2xx) o de operadora (móvil,
  // 4xx) + 7 del número = 10 en total. El 0 de marcación nacional NO va: es un
  // prefijo para llamar dentro del país, no parte del número que viaja con el
  // +58. Es la misma regla que ya usan el resto de las pantallas del sistema.
  if (phoneRaw.startsWith('0'))
    return `No incluyas el 0 inicial: son ${VE_LOCAL_DIGITS} dígitos, 3 del código de área y 7 del número (ej. 2121234567)`

  if (phoneRaw.length === 7)
    return 'Falta el código de área o de operadora (3 dígitos)'

  if (phoneRaw.length !== VE_LOCAL_DIGITS) {
    if (phoneRaw.length < VE_LOCAL_DIGITS)
      return `Número incompleto: son ${VE_LOCAL_DIGITS} dígitos, 3 del código de área y 7 del número (ej. 2121234567)`
    // 12+ dígitos es la firma de un número internacional cuyo "+" se perdió al
    // leer la celda como número ("+57 300…" → 573001234567). Decir solo
    // "demasiado largo" manda al operador a borrar dígitos buenos.
    return phoneRaw.length >= 12
      ? `Teléfono demasiado largo. ${INTERNATIONAL_PHONE_HINT}`
      : `Teléfono demasiado largo: son ${VE_LOCAL_DIGITS} dígitos, 3 del código de área y 7 del número`
  }

  // El código de país repetido DENTRO del número ("05841286648" → "5841286648")
  // es un error real y recurrente, y con 10 dígitos pasaría desapercibido.
  if (phoneRaw.startsWith('58'))
    return 'El número no debe repetir el código de país (+58): son 3 dígitos de código de área y 7 del número'

  // Códigos de área venezolanos: 2xx y 3xx para fijos (212, 244, 295, 347, 376…)
  // y 4xx para móviles. Lo que empiece por otro dígito no existe. El resto —qué
  // combinación exacta es válida— lo decide el backend con libphonenumber; el
  // front no lleva la tabla completa de códigos de área (ver TODOS.md: el
  // catálogo /api/phone/area-code hoy solo devuelve los 6 prefijos móviles).
  if (!/^[234]/.test(phoneRaw))
    return 'El código de área debe empezar por 2 o 3 (fijo) o 4 (móvil)'

  // Red de seguridad: paridad exacta con el regex del backend, por si alguna rama
  // anterior dejara pasar un valor que el backend rechazaría (evita el 500 al enviar).
  if (!isBackendValidPhone(phoneRaw))
    return 'Teléfono inválido para el sistema (9 a 15 dígitos)'

  return null
}

// ---------------------------------------------------------------------------
// Nombres de cliente (remitente / destinatario): paridad con el validador del
// backend, que responde `account.Account.1` con el texto
//   "Los nombres de cliente solo pueden contener letras, números, guiones,
//    puntos, comas, apóstrofes y &".
// Validarlo acá evita mandar la carga para que vuelva en error.
//
// 2026-09-03: la Ñ se PERMITE por pedido del equipo (el punto ya estaba
// permitido).
// 2026-09-07: las reglas pasan a depender del TIPO DE PERSONA, en paridad con
// el cambio equivalente de API Connect (tealca-api-django):
//   - Jurídico (RIF `J`, gubernamental `G`, comuna `C`): se acepta además `+`
//     ("Comercializadora +3") y NO se exige el mínimo de 2 palabras (los
//     nombres comerciales de una sola palabra —SHEIN, ROCKAUTO, AMAZON— son
//     legítimos).
//   - Natural (`V`, `E`, `P` o desconocido): charset sin `+` y mínimo de 2
//     palabras, como hasta ahora.
// Los acentos y la `ü` se aceptan en AMBOS casos: el backend siempre los aceptó
// y el front los estaba rechazando de más (bloqueaba nombres que la API toma).
// ---------------------------------------------------------------------------

/** Forma mínima del catálogo `state.inmutable.taxIdentificationTypes`. */
export interface IdentificationTypeLike {
  taxIdentificationTypeId: string | number
  abbreviationName: string
}

/**
 * Prefijos de identificación que el backend trata como persona JURÍDICA: RIF
 * (`J`), gubernamental (`G`) y comuna (`C`).
 *
 * Se decide por el prefijo y NO por el flag `isJuridic` del catálogo:
 * `getTaxIdentificationTypes` (src/services/AccountServices.tsx) está hardcodeado
 * y trae ese flag invertido (V-, P- y E- marcados `true`; G- y J-, `false`).
 *
 * `C` es a futuro: ese mismo catálogo hardcodeado devuelve HOY solo V-, P-, G-, J- y
 * E-, así que por la vía del catálogo el prefijo C nunca se alcanza. Se deja para que
 * la regla no haya que tocarla cuando el tipo Comuna se agregue.
 */
const JURIDIC_ID_PREFIXES = ['J', 'G', 'C']

/**
 * ¿La abreviación de tipo de documento corresponde a una persona jurídica?
 * Tolera las dos formas en que el catálogo devuelve la abreviación: con guion
 * final (`'J-'`, tal cual sale del catálogo) y sin él (`'J'`, como queda en el
 * payload).
 */
export function isJuridicIdentification(
  abbr?: string | null,
): boolean {
  const first = (abbr || '').trim().charAt(0).toUpperCase()
  if (!first) return false
  return JURIDIC_ID_PREFIXES.indexOf(first) !== -1
}

/**
 * ¿El id de tipo de documento de una guía (viene stringificado en el grupo)
 * corresponde a una persona jurídica? Resuelve el id contra el catálogo con el
 * mismo lookup que `docTypeAbbreviation` (utils/bulkTemplateRows) y delega en
 * `isJuridicIdentification`, que es el predicado único de la regla: quien ya tiene
 * la entrada del catálogo resuelta (p. ej. ShipmentBulkUploadAccordionRow) llama
 * directo a ese, no a este.
 *
 * Sin catálogo o con un id que no resuelve devuelve `false` (natural), que es
 * la regla más estricta: en la duda se valida de más y el operador ve el error
 * en el editor en vez de que la guía vuelva rechazada del backend.
 */
export function isJuridicDocType(
  identificationTypes: IdentificationTypeLike[] | undefined,
  docTypeId?: string | null,
): boolean {
  if (!identificationTypes?.length || !docTypeId) return false
  const t = identificationTypes.find(
    (x) => String(x.taxIdentificationTypeId) === String(docTypeId),
  )
  return isJuridicIdentification(t?.abbreviationName)
}

/** Opciones de las validaciones de nombre: distinguen persona natural de jurídica. */
export interface ClientNameOptions {
  juridic?: boolean
}

/**
 * Símbolos permitidos además de letras, dígitos y espacios, SIN el guión (ver abajo).
 * Única fuente: de acá salen TANTO la clase de caracteres del regex COMO el texto del
 * mensaje de error. Manteniéndolos por separado ya se desincronizaron una vez: el
 * mensaje viejo omitía el punto y decía "sin acentos" cuando el punto sí estaba
 * permitido y los acentos también.
 */
const NAME_SYMBOLS = ['.', ',', "'", '&'] as const
const NAME_SYMBOLS_JURIDIC = [...NAME_SYMBOLS, '+'] as const

/**
 * Letras, dígitos y espacios de la clase de caracteres, en paridad EXACTA con el
 * regex del backend (`AccountService.check_account_info`, tealca-api-django):
 *   ^[a-zA-Z0-9\s\-\.,'&áéíóúÁÉÍÓÚüÜñÑ]+$
 *
 * Ojo con `\s`: el backend acepta CUALQUIER espacio Unicode — el tabulador y sobre
 * todo el espacio duro U+00A0, que es lo que sale de pegar un nombre desde Excel. Con
 * un espacio literal acá el editor bloquearía nombres que la API sí toma.
 */
const NAME_CLASS_BASE = "A-Za-zÁÉÍÓÚáéíóúÜüÑñ0-9\\s"

/**
 * Clase de caracteres del nombre. El `-` se agrega SIEMPRE al final: en cualquier otra
 * posición `[...&-+]` se leería como rango y el regex explota ("Range out of order").
 */
const nameCharClass = (symbols: readonly string[]): RegExp =>
  new RegExp('[' + NAME_CLASS_BASE + symbols.join('') + '-]')

/** El mensaje, en cambio, lista el guión PRIMERO (como venía). */
const symbolsLabel = (symbols: readonly string[]): string =>
  ['-', ...symbols].join(' ')

/** Caracteres que el backend acepta en el nombre de una persona natural. */
const CLIENT_NAME_ALLOWED_RE = nameCharClass(NAME_SYMBOLS)

/** Ídem para persona jurídica: lo mismo más `+`. */
const CLIENT_NAME_ALLOWED_JURIDIC_RE = nameCharClass(NAME_SYMBOLS_JURIDIC)

/**
 * ¿El carácter es invisible al imprimirlo? Interesa para el MENSAJE: un ofensor que no
 * se ve (marca combinante de un acento descompuesto —NFD, lo que produce Excel en
 * macOS—, un zero-width, un control) salía en el texto como un hueco y el operador no
 * tenía forma de saber qué borrar.
 *
 * NO se normaliza el nombre a NFC para "arreglarlo": el backend compara contra vocales
 * PRECOMPUESTAS, así que un nombre en NFD lo rechaza igual. Aceptarlo acá solo mandaría
 * la guía a fallar del otro lado.
 */
const isInvisibleChar = (ch: string): boolean => {
  const cp = ch.codePointAt(0) ?? 0
  return (
    cp < 0x20 || // controles
    (cp >= 0x0300 && cp <= 0x036f) || // marcas combinantes (acentos NFD)
    cp === 0x200b || // zero-width space
    cp === 0xfeff // BOM
  )
}

/** Etiqueta legible de un carácter ofensor; los invisibles se muestran como `U+XXXX`. */
const charLabel = (ch: string): string =>
  isInvisibleChar(ch)
    ? `U+${(ch.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}`
    : ch

/** Caracteres distintos del nombre que el backend NO acepta, en orden de aparición. */
export function invalidClientNameChars(
  name: string | null | undefined,
  opts?: ClientNameOptions,
): string[] {
  const allowed = opts?.juridic
    ? CLIENT_NAME_ALLOWED_JURIDIC_RE
    : CLIENT_NAME_ALLOWED_RE
  const seen: string[] = []
  for (const ch of (name || '').trim()) {
    if (!allowed.test(ch) && seen.indexOf(ch) === -1) seen.push(ch)
  }
  return seen
}

/** Mensaje para un nombre con caracteres no permitidos, o `null` si está bien. */
export function clientNameError(
  name: string | null | undefined,
  opts?: ClientNameOptions,
): string | null {
  const bad = invalidClientNameChars(name, opts)
  if (bad.length === 0) return null
  const allowed = symbolsLabel(
    opts?.juridic ? NAME_SYMBOLS_JURIDIC : NAME_SYMBOLS,
  )
  return `No se permite ${bad.map(charLabel).join(' ')} — solo letras, números, espacios y ${allowed}`
}

/**
 * El backend también exige que el nombre de una persona NATURAL tenga AL MENOS
 * 2 PALABRAS y responde `account.Account.2` con "Los nombres de cliente deben
 * tener al menos 2 palabras". Validarlo acá evita mandar la carga para que
 * vuelva en error, igual que el charset de arriba.
 *
 * Para persona jurídica NO hay mínimo: "SHEIN" o "ROCKAUTO" son nombres válidos.
 *
 * El vacío devuelve `null` a propósito: eso ya lo cubre "Requerido".
 */
export function clientNameWordCountError(
  name: string | null | undefined,
  opts?: ClientNameOptions,
): string | null {
  if (opts?.juridic) return null
  const words = (name || '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0 || words.length >= 2) return null
  return 'El nombre debe tener al menos 2 palabras (ej. nombre y apellido)'
}

/**
 * Mínimo valor declarado exigido para un peso+servicio, según la tabla de rangos del
 * sistema (state.inmutable.rangeDeclareList). Paridad con la lógica de shipments/create
 * (CreateShipmentV2.minDeclaredValue): busca el rango del servicio; si no hay rango
 * específico, cae al rango genérico (serviceID === 0). Devuelve 0 si ningún rango aplica.
 */
export function minDeclaredValue(
  weight: number,
  rangeList: RangeDeclareDto[],
  service?: number,
): number {
  let filtered = rangeList.filter((r) => r.serviceID === service)
  if (filtered.length === 0)
    filtered = rangeList.filter((r) => r.serviceID === 0)
  return (
    filtered.find((r) => r.minWeight < weight && weight <= r.maxWeight)
      ?.minDeclareValue ?? 0
  )
}

export interface ValidateGroupContext {
  citiesByBu: any[]
  businessUnits: any[]
  isCOD: boolean
  rangeDeclareList: RangeDeclareDto[]
  valueDeclareServiceIds?: Set<number>
  // Oficinas origen usables del contrato (para validar el origen POR FILA).
  originBusinessUnits?: any[]
  // buCodes de las tiendas destino que el contrato corporativo permite, ya
  // resueltos con `resolveDestinationBusinessUnits` (AccountSiteBU). Vacío = el
  // contrato no restringe. Ver utils/destinationBusinessUnits.
  allowedDestinationBuCodes?: string[]
  // buCodes que NO son elegibles como retiro en Tienda por su TIPO de tienda (hoy:
  // Junior). Complementa a allowedDestinationBuCodes, que solo se llena cuando el
  // contrato restringe: sin él, un cliente sin contrato corporativo podría escribir a
  // mano en el Excel una tienda que el desplegable ya no ofrece y nadie lo notaría.
  // Las Junior que el contrato SÍ habilita no entran acá (ver ShipmentBulkUploadReview).
  blockedStoreCodes?: string[]
  // Topes por dimensión del Facturador; se reenvían a validatePiece para que la
  // guía quede marcada en error cuando una de sus piezas supera el tope.
  dimensionMax?: PieceDimensionMax
  // Factor volumétrico del sistema (cm³/kg), sondeado una vez vía GetChargedWeight.
  // Con él se calcula el peso a facturar de la guía (mayor entre balanza y
  // volumétrico) y se compara contra el maxWeight del servicio — es el rechazo
  // shipping.ShipmentHeader.35 del backend, atajado en el review. Sin factor
  // (sonda fallida) solo se valida el peso físico contra el tope.
  volumetricFactor?: number | null
  // Servicios autorizados con su tope de peso; el de la guía se busca por serviceId.
  services?: { serviceID: number; maxWeight?: number }[]
  // `state.inmutable.taxIdentificationTypes`: resuelve el tipo de documento de cada
  // parte a su abreviación para decidir si el nombre se valida como persona jurídica
  // (ver isJuridicDocType). Sin catálogo, todo se valida como persona natural.
  identificationTypes?: IdentificationTypeLike[]
}

/**
 * Pesos de una guía de carga masiva, con la MISMA aritmética que el backend:
 * físico = Σ peso × cantidad; volumétrico = Σ (alto × ancho × largo / factor) ×
 * cantidad (cada fila entra "cantidad" veces, igual que al expandir las piezas);
 * a facturar = el mayor de los dos. Sin factor no hay volumétrico (queda en 0).
 */
export function bulkChargedWeight(
  pieces: ShipmentPieceRow[],
  volumetricFactor?: number | null,
): { physical: number; dimensional: number; charged: number } {
  const physical = pieces.reduce(
    (acc, p) => acc + (p.weight || 0) * (p.quantity || 1),
    0,
  )
  const dimensional =
    volumetricFactor && volumetricFactor > 0
      ? pieces.reduce(
          (acc, p) =>
            acc +
            (((p.height || 0) * (p.width || 0) * (p.length || 0)) /
              volumetricFactor) *
              (p.quantity || 1),
          0,
        )
      : 0
  return { physical, dimensional, charged: Math.max(physical, dimensional) }
}

export function validateGroup(
  g: ShipmentGroup,
  ctx: ValidateGroupContext,
): ShipmentGroupErrors {
  const {
    citiesByBu,
    isCOD,
    rangeDeclareList,
    valueDeclareServiceIds,
    originBusinessUnits = [],
    allowedDestinationBuCodes = [],
    blockedStoreCodes = [],
    dimensionMax,
    volumetricFactor,
    services,
    identificationTypes,
  } = ctx
  const e: ShipmentGroupErrors = {}

  // [F1/F2] Origen por fila: solo cuando el contrato tiene VARIAS oficinas hay que
  // elegir. Vacío o buCode no resoluble contra el catálogo → error de fila (nunca
  // fallback silencioso). Con 1 sola oficina se auto-asigna (no se exige).
  if (originBusinessUnits.length > 1) {
    if (!g.originStoreCode?.trim()) {
      e.originStoreCode = 'Selecciona la oficina origen'
    } else if (
      !originBusinessUnits.some((b) => b.buCode === g.originStoreCode)
    ) {
      e.originStoreCode = 'Oficina origen no válida para este contrato'
    }
  }
  if (!g.shipmentHeaderReference?.trim())
    e.shipmentHeaderReference = 'Requerido'
  else if (g.shipmentHeaderReference.trim().length > 30)
    // El input manual está capado a 30 (maxLength), pero las guías cargadas desde Excel
    // no pasan por ese cap; se marcan aquí para que el operador las corrija antes de enviar.
    e.shipmentHeaderReference = 'Máximo 30 caracteres'
  if (!g.deliveryType) e.deliveryType = 'Requerido'

  if (g.deliveryType === DeliveryEnum.DELIVERY) {
    if (!g.destinationCity?.trim()) {
      e.destinationCity = 'Requerido para domicilio'
    } else {
      // citiesByBu (Django, GET /api/location → default_destination_store) ES la tabla
      // autoritativa ciudad→tienda destino. NO cruzar contra businessUnits: basta con que
      // la ciudad tenga BU asignada aquí.
      const cityBuCode = citiesByBu.find(
        (c) => c.locationCode === g.destinationCity,
      )?.buCode
      if (!cityBuCode) {
        e.destinationCity = 'Ciudad sin tienda asignada en el sistema'
      }
    }
  }
  if (g.deliveryType === DeliveryEnum.OFFICE) {
    const sameBuCode = (a: unknown, b: unknown): boolean =>
      a?.toString().trim().toUpperCase() === b?.toString().trim().toUpperCase()

    if (!g.storeCode?.trim()) {
      e.storeCode = 'Requerido para retiro por oficina'
    } else if (
      // Tipo de tienda no elegible (Junior). Va ANTES del chequeo de contrato porque es
      // el motivo más específico: si la tienda no admite retiro, da igual que el
      // contrato la liste o no. Igual que el de abajo, solo aplica en retiro en tienda:
      // a domicilio una Junior es un destino perfectamente válido.
      blockedStoreCodes.some((code) => sameBuCode(code, g.storeCode))
    ) {
      e.storeCode = 'Esta tienda no admite retiro en tienda'
    } else if (
      // Tienda destino restringida por contrato: solo se valida en retiro en tienda,
      // que es donde el operador ELIGE la tienda. En domicilio el storeCode lo deriva
      // el sistema desde citiesByBu, así que exigirlo ahí bloquearía guías válidas.
      allowedDestinationBuCodes.length > 0 &&
      !allowedDestinationBuCodes.some((code) => sameBuCode(code, g.storeCode))
    ) {
      e.storeCode = 'Tienda destino no permitida para este contrato'
    }
  }

  if (!isCOD) {
    if (!g.shipperDocType?.trim()) e.shipperDocType = 'Requerido'
    if (!g.shipperDocNumber?.trim()) e.shipperDocNumber = 'Requerido'
    if (!g.shipperName?.trim()) e.shipperName = 'Requerido'
    else {
      const opts = {
        juridic: isJuridicDocType(identificationTypes, g.shipperDocType),
      }
      const nameErr =
        clientNameError(g.shipperName, opts) ||
        clientNameWordCountError(g.shipperName, opts)
      if (nameErr) e.shipperName = nameErr
    }
  }

  if (!g.consigneeDocType?.trim()) e.consigneeDocType = 'Requerido'
  if (!g.consigneeDocNumber?.trim()) e.consigneeDocNumber = 'Requerido'
  if (!g.consigneeName?.trim()) e.consigneeName = 'Requerido'
  else {
    const opts = {
      juridic: isJuridicDocType(identificationTypes, g.consigneeDocType),
    }
    const nameErr =
      clientNameError(g.consigneeName, opts) ||
      clientNameWordCountError(g.consigneeName, opts)
    if (nameErr) e.consigneeName = nameErr
  }

  // V3: reglas y textos en `consigneePhoneError` — compartidas con la traducción
  // del error de ejecución del backend para que el campo diga siempre lo mismo.
  // El país decide qué reglas corren (Venezuela tiene operadora; el resto no).
  const phoneErr = consigneePhoneError(
    g.consigneePhone,
    consigneeCountryCode(g),
  )
  if (phoneErr) e.consigneePhone = phoneErr

  if (
    g.consigneeEmail?.trim() &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(g.consigneeEmail.trim())
  )
    e.consigneeEmail = 'Formato inválido'

  if (!g.destinationAddress?.trim()) e.destinationAddress = 'Requerido'

  if (g.latitude != null || g.longitude != null) {
    if (!isValidCoord(g.latitude, -90, 90))
      e.latitude = 'Latitud inválida (-90 a 90)'
    if (!isValidCoord(g.longitude, -180, 180))
      e.longitude = 'Longitud inválida (-180 a 180)'
  }

  // Piezas: además de exigir al menos una, validamos CADA pieza (peso, dimensiones,
  // valor declarado —incluido el mínimo por peso+servicio—, categoría, cantidad). El
  // mínimo se valida POR PIEZA (cada pieza/categoría puede tener un valor distinto), por
  // eso le pasamos el contexto a validatePiece. Si alguna pieza falla, se marca la fila
  // para que no se pueda enviar (el detalle por pieza se ve al expandir).
  const requireDeclaredValue =
    isCOD ||
    (g.serviceId != null && (valueDeclareServiceIds?.has(g.serviceId) ?? false))
  if (!g.pieces?.length) {
    e.pieces = 'Debe tener al menos una pieza'
  } else if (
    g.pieces.some(
      (p) =>
        Object.keys(
          validatePiece(p, {
            rangeDeclareList,
            serviceId: g.serviceId,
            requireDeclaredValue,
            dimensionMax,
          }),
        ).length > 0,
    )
  ) {
    // Sin texto: el detalle se muestra POR PIEZA en cada card. e.pieces queda como cadena
    // vacía (no se renderiza, pero mantiene la guía marcada/bloqueada en hasAnyError).
    e.pieces = ''
  }

  // Peso a facturar de la guía contra el tope del servicio (paridad con
  // shipments/create y con el rechazo shipping.ShipmentHeader.35 del backend).
  // Pisa el marcador '' a propósito: este texto es más útil que el silencio, y
  // los errores por pieza igual se ven en cada card.
  const serviceMaxWeight = services?.find(
    (s) => s.serviceID === g.serviceId,
  )?.maxWeight
  if (serviceMaxWeight && g.pieces?.length) {
    const { physical, dimensional, charged } = bulkChargedWeight(
      g.pieces,
      volumetricFactor,
    )
    if (charged > serviceMaxWeight) {
      e.pieces = `El peso a facturar de la guía (${charged.toFixed(
        2,
      )}kg) supera el máximo permitido (${serviceMaxWeight}kg) — ${
        dimensional > physical
          ? 'disminuya las dimensiones de las piezas o divida la guía'
          : 'disminuya el peso de las piezas o divida la guía'
      }`
    }
  }

  return e
}

/**
 * Tope de dimensiones por pieza, en cm. Sale del parámetro `dimensionMax` de
 * GET /Application/Facturador (MAXLARGO / MAXALTO / MAXANCHO, configurables en BD
 * por país). Antes estaba cableado en 300 en shipments/create y aquí; ahora el valor
 * lo manda el backend y, si el parámetro no viene configurado para una dimensión,
 * esa dimensión NO tiene tope.
 *
 * En la carga masiva la regla corre SOLO en el review, que es donde el operador
 * todavía puede corregir la línea antes de enviarla.
 */
export const EMPTY_PIECE_DIMENSION_MAX: PieceDimensionMax = {
  height: null,
  width: null,
  length: null,
}

/** "300" → 300. Cualquier valor ausente, vacío o no numérico → null (sin tope). */
const parseDimensionValue = (
  param: { valueGeneral?: string | null } | null | undefined,
): number | null => {
  const raw = param?.valueGeneral
  if (raw === null || raw === undefined || `${raw}`.trim() === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/** DTO crudo del Facturador → topes numéricos por dimensión. */
export function parsePieceDimensionMax(
  dto: DimensionMaxDto | null | undefined,
): PieceDimensionMax {
  if (!dto) return EMPTY_PIECE_DIMENSION_MAX
  return {
    height: parseDimensionValue(dto.height),
    width: parseDimensionValue(dto.width),
    length: parseDimensionValue(dto.length),
  }
}

export const maxPieceDimensionMessage = (max: number): string =>
  `No puede superar los ${max} cm`

export interface ValidatePieceContext {
  rangeDeclareList: RangeDeclareDto[]
  serviceId?: number
  requireDeclaredValue?: boolean
  /**
   * Topes por dimensión del Facturador. Opcional: sin él (o con dimensiones en
   * null) solo corre la regla "> 0".
   */
  dimensionMax?: PieceDimensionMax
  /**
   * La GUÍA (no esta pieza) supera el peso a facturar máximo del servicio; lo
   * calcula el caller con `bulkChargedWeight` sobre las piezas candidatas. Marca
   * en rojo los campos que hay que bajar: dimensiones si excede el volumétrico,
   * peso si excede la balanza (paridad con el PieceForm de shipments/create).
   */
  chargedOverweight?: { byDimensions: boolean } | null
}

export function validatePiece(
  p: ShipmentPieceRow,
  ctx?: ValidatePieceContext,
): Partial<Record<keyof ShipmentPieceRow, string>> {
  const e: Partial<Record<keyof ShipmentPieceRow, string>> = {}
  if (!p.quantity || p.quantity < 1) e.quantity = 'Debe ser al menos 1'
  if (!p.weight || p.weight <= 0) e.weight = 'Debe ser mayor a 0'
  const dimensionMax = ctx?.dimensionMax ?? EMPTY_PIECE_DIMENSION_MAX
  if (!p.height || p.height <= 0) e.height = 'Debe ser mayor a 0'
  else if (dimensionMax.height !== null && p.height > dimensionMax.height)
    e.height = maxPieceDimensionMessage(dimensionMax.height)
  if (!p.width || p.width <= 0) e.width = 'Debe ser mayor a 0'
  else if (dimensionMax.width !== null && p.width > dimensionMax.width)
    e.width = maxPieceDimensionMessage(dimensionMax.width)
  if (!p.length || p.length <= 0) e.length = 'Debe ser mayor a 0'
  else if (dimensionMax.length !== null && p.length > dimensionMax.length)
    e.length = maxPieceDimensionMessage(dimensionMax.length)
  // Paridad con shipments/create: el valor declarado (y su mínimo) SOLO se exige cuando la
  // guía viaja asegurada — COD (siempre) o servicio con isValueDeclare. Si no aplica, el
  // operador puede dejar 0 y no se valida el mínimo. Default true por retrocompatibilidad.
  const requireDeclaredValue = ctx?.requireDeclaredValue ?? true
  if (requireDeclaredValue) {
    if (!p.declaredValue || p.declaredValue <= 0) {
      e.declaredValue = 'Debe ser mayor a 0'
    } else if (ctx?.rangeDeclareList) {
      // Mismo criterio que la creación de guía individual: el rango se busca por el peso
      // FACTURABLE de la línea (peso × cantidad) y devuelve el mínimo TOTAL. Como el campo es
      // por pieza, dividimos ese total entre la cantidad para indicarle cuánto poner por pieza
      // (redondeado hacia arriba al centavo, para que el valor mostrado siempre cumpla).
      const qty = p.quantity && p.quantity > 0 ? p.quantity : 1
      const totalMin = minDeclaredValue(
        (p.weight || 0) * qty,
        ctx.rangeDeclareList,
        ctx.serviceId,
      )
      if (totalMin > 0) {
        const perPieceMin = Math.ceil((totalMin / qty) * 100) / 100
        if (p.declaredValue < perPieceMin)
          e.declaredValue = `El valor declarado por pieza debe ser al menos ${perPieceMin.toFixed(
            2,
          )}$ (mínimo total ${totalMin.toFixed(2)}$ para ${qty} pieza(s)).`
      }
    }
  }
  if (!p.categoryId) e.categoryId = 'Requerido'

  // Sobrepeso de la guía: se marca el campo culpable de CADA pieza sin pisar un
  // error propio ya puesto (ese es más específico). El mismo texto en alto/ancho/
  // largo se deduplica en la card.
  if (ctx?.chargedOverweight) {
    if (ctx.chargedOverweight.byDimensions) {
      const msg =
        'El peso a facturar de la guía supera el máximo — disminuya las dimensiones'
      if (!e.height) e.height = msg
      if (!e.width) e.width = msg
      if (!e.length) e.length = msg
    } else if (!e.weight) {
      e.weight =
        'El peso a facturar de la guía supera el máximo — disminuya el peso'
    }
  }

  return e
}
